<template>
  <div class="scan-outbound">
    <div v-if="!orderId" class="idle-wrap">
      <div class="scan-btn" @click="openScanner">
        <van-icon name="scan" size="56" color="#fff" />
      </div>
      <div class="idle-tip">请扫描商家收款码</div>
      <van-button plain round class="back-entry-btn" @click="$emit('back')">
        返回
      </van-button>
    </div>

    <outbound-pay
      v-else
      :order-id="orderId"
      :order-info="orderInfo"
      :products="products"
      back-text="重新扫码"
      @back="resetToScan"
    />

    <van-popup
      v-model="showScanner"
      position="bottom"
      :style="{ height: '100%' }"
      get-container="body"
      :close-on-click-overlay="false"
      @opened="onScannerOpened"
      @closed="onScannerClosed"
    >
      <div class="scanner-page">
        <div class="scanner-nav">
          <div class="scanner-back" @click="closeScanner">
            <van-icon name="arrow-left" size="20" color="#fff" />
            <span>返回</span>
          </div>
          <div class="scanner-title">扫码出库</div>
          <div class="scanner-nav-right"></div>
        </div>
        <!-- plus.barcode 原生预览挂载点，仅占导航下方区域 -->
        <div id="manual-outbound-barcode" class="camera-preview"></div>
        <div class="scanner-frame">
          <div class="corner top-left"></div>
          <div class="corner top-right"></div>
          <div class="corner bottom-left"></div>
          <div class="corner bottom-right"></div>
        </div>
        <div class="scanner-tip">请扫描商家收款码</div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from "vant";
import OutboundPay from "./outbound-pay.vue";
import { loadOutboundOrder } from "./outbound-common";

const BARCODE_ERROR_CODES = {
  8: "摄像头权限不足或摄像头被占用",
  9: "扫码超时",
  10: "扫码被用户取消",
  11: "设备不支持扫码",
  12: "扫码参数错误",
};

export default {
  name: "scan-outbound",
  components: { OutboundPay },
  data() {
    return {
      showScanner: false,
      scanLocked: false,
      barcodeScanner: null,
      orderId: "",
      merchId: "",
      orderInfo: {},
      products: [],
    };
  },
  beforeDestroy() {
    this.cleanupBarcode();
  },
  methods: {
    openScanner() {
      this.scanLocked = false;
      if (typeof window.plus === "undefined" || !window.plus.barcode) {
        Toast.fail("当前环境不支持扫码，请在 App 容器中运行");
        return;
      }
      this.showScanner = true;
    },
    closeScanner() {
      this.cleanupBarcode();
      this.showScanner = false;
    },
    onScannerOpened() {
      // 弹层打开后再启原生扫码，保证挂载点已布局
      this.$nextTick(() => {
        setTimeout(() => this.startPlusScan(), 80);
      });
    },
    onScannerClosed() {
      this.cleanupBarcode();
      this.scanLocked = false;
    },
    cleanupBarcode() {
      if (this.barcodeScanner) {
        try {
          this.barcodeScanner.close();
        } catch (e) {
          // 忽略重复关闭
        }
        this.barcodeScanner = null;
      }
    },
    /** Android 请求摄像头权限后启动扫码 */
    startPlusScan() {
      this.requestCameraPermission().then(() => {
        this.tryPlusBarcodeScan();
      });
    },
    requestCameraPermission() {
      return new Promise((resolve) => {
        try {
          if (window.plus && window.plus.android) {
            window.plus.android.requestPermissions(
              ["android.permission.CAMERA"],
              (e) => {
                if (!(e.granted && e.granted.length > 0)) {
                  Toast.fail("摄像头权限被拒绝，请在设置中开启");
                }
                resolve();
              },
              () => resolve()
            );
          } else {
            resolve();
          }
        } catch (e) {
          resolve();
        }
      });
    },
    /** 5+App：plus.barcode.Barcode 控件扫码 */
    tryPlusBarcodeScan() {
      this.cleanupBarcode();
      try {
        const filters = [window.plus.barcode.QR];
        const scanner = new window.plus.barcode.Barcode(
          "manual-outbound-barcode",
          filters
        );
        this.barcodeScanner = scanner;

        scanner.onmarked = (type, result) => {
          this.cleanupBarcode();
          this.onScanDecode(result);
        };
        scanner.onerror = (error) => {
          this.cleanupBarcode();
          const code = error && error.code;
          const tip =
            BARCODE_ERROR_CODES[code] || (error && error.message) || "未知错误";
          Toast.fail(`扫码失败：${tip}`);
          this.closeScanner();
        };
        scanner.start();
      } catch (e) {
        // 降级：全屏原生扫码
        this.tryTraditionalScan();
      }
    },
    /** 降级：plus.barcode.scan */
    tryTraditionalScan() {
      try {
        window.plus.barcode.scan(
          (type, result) => {
            // 兼容不同运行时：有的只回传一个字符串
            const text = result != null ? result : type;
            this.onScanDecode(text);
          },
          (error) => {
            const code = error && error.code;
            const tip =
              BARCODE_ERROR_CODES[code] ||
              (error && error.message) ||
              "未知错误";
            Toast.fail(`扫码失败：${tip}`);
            this.closeScanner();
          },
          [window.plus.barcode.QR]
        );
      } catch (e) {
        Toast.fail("启动扫码失败，请检查 App 是否启用 Barcode 模块");
        this.closeScanner();
      }
    },
    /**
     * 解析收款码参数（与 pay/pages/new-gf/user/user.vue 一致）
     * 例：http://erp.gfxtqqg.com/payment?merchId=2024&orderId=2081585492058574848
     */
    parsePayQr(raw) {
      const text = decodeURIComponent(String(raw || "").trim());
      const query = this.getUrlParams(text);
      const merchId =
        query.merchid || query.merchId || query.merch_id || "";
      const orderId =
        query.orderid || query.orderId || query.order_id || "";
      return { merchId, orderId };
    },
    getUrlParams(url) {
      const paramsObj = {};
      const idx = String(url).indexOf("?");
      if (idx < 0) return paramsObj;
      String(url)
        .slice(idx + 1)
        .split("&")
        .forEach((param) => {
          if (!param) return;
          const parts = param.split("=");
          const key = parts[0];
          const value = parts.slice(1).join("=");
          if (key) paramsObj[key] = value;
        });
      return paramsObj;
    },
    async onScanDecode(decodedString) {
      if (this.scanLocked) return;
      this.scanLocked = true;

      // 与收款吧一致：扫码只解析参数
      const { merchId, orderId } = this.parsePayQr(decodedString);
      // App 控制台无法展开对象，必须 stringify
      console.log(
        "[扫码出库] 扫码参数 " +
          JSON.stringify({ merchId, orderId, raw: decodedString })
      );
      if (!merchId) {
        Toast.fail("扫码获取参数失败");
        this.scanLocked = false;
        if (this.showScanner) {
          this.$nextTick(() => this.startPlusScan());
        }
        return;
      }

      this.closeScanner();
      await this.loadByParams(merchId, orderId);
    },
    /** 必须带 orderId；merchId 仅用于校验收款码格式 */
    async loadByParams(merchId, orderId) {
      this.merchId = String(merchId);
      this.orderId = orderId ? String(orderId) : "";
      this.products = [];
      this.orderInfo = {};

      if (!this.orderId) {
        Toast.fail("请扫描带订单的收款码");
        this.scanLocked = false;
        return;
      }

      const toast = Toast.loading({
        message: "加载订单中...",
        forbidClick: true,
        duration: 0,
      });
      try {
        const loaded = await loadOutboundOrder(this.orderId);
        this.orderInfo = loaded.orderInfo;
        this.products = loaded.products;
      } catch (e) {
        const status = e && e.response && e.response.status;
        const msg = (e && (e.msg || e.message)) || "";
        console.error(
          "[扫码出库] 查单失败 " +
            JSON.stringify({
              merchId: this.merchId,
              orderId: this.orderId,
              status,
              msg,
            })
        );
        Toast.fail(msg || "加载订单失败");
        this.orderId = "";
      } finally {
        toast.clear();
        this.scanLocked = false;
      }
    },
    resetToScan() {
      this.orderId = "";
      this.merchId = "";
      this.orderInfo = {};
      this.products = [];
      this.$nextTick(() => this.openScanner());
    },
  },
};
</script>

<style scoped lang="less">
.idle-wrap {
  min-height: calc(100vh - 46px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.scan-btn {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #3efe4b;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.idle-tip {
  margin-top: 20px;
  font-size: 15px;
  color: #646566;
}

.back-entry-btn {
  margin-top: 24px;
  width: 70%;
}

.scanner-page {
  height: 100%;
  background: #000;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 顶部返回栏必须在原生摄像头区域之外，否则 plus.barcode 会盖住点不到 */
.scanner-nav {
  flex-shrink: 0;
  height: 46px;
  padding-top: env(safe-area-inset-top);
  box-sizing: content-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 8px;
  padding-right: 8px;
  background: #1989fa;
  position: relative;
  z-index: 20;
}

.scanner-back {
  min-width: 72px;
  height: 46px;
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 15px;
  padding-left: 4px;
}

.scanner-back span {
  margin-left: 2px;
}

.scanner-title {
  flex: 1;
  text-align: center;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.scanner-nav-right {
  min-width: 72px;
}

.camera-preview {
  flex: 1;
  position: relative;
  width: 100%;
  min-height: 0;
  background: #000;
}

.scanner-frame {
  position: absolute;
  top: calc(50% + 23px);
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  z-index: 5;
  pointer-events: none;
}

.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #3efe4b;
}

.top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
}

.bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.scanner-tip {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 80px;
  z-index: 5;
  text-align: center;
  color: #fff;
  font-size: 14px;
}
</style>
